import {failure} from '@danwang/either';
import {extract} from '@danwang/extractor';
import extractorError from '@danwang/extractor/lib/error';
import {DateTime} from 'luxon';
import {parseJson} from 'src/parseJson';
import type {ExtractResult, Extractor} from '@danwang/extractor';

export const EZPASS_SOURCE = 'ezpass';

export type EzpassResult = {
  amount: number;
  date: string;
  description: string;
  /** Id space for externalId — see statements/SPEC.md, "External ids". */
  externalSource: string;
  /** E-ZPass's own compositeId; the dedupe key on import. */
  externalId: string;
  /** The scraped row, verbatim — sent as-is, not stringified. */
  ezpassData: EzpassTransaction;
};

const parseEzpassDate = (ezpassDate: string): string => {
  // EZPass format: "01/06/2026 06:40:54.903" or "01/06/2026"
  const parsed = DateTime.fromFormat(
    ezpassDate,
    ezpassDate.includes(' ') ? 'MM/dd/yyyy HH:mm:ss.SSS' : 'MM/dd/yyyy',
    {zone: 'America/New_York'},
  );
  return parsed.toISODate();
};

export type EzpassTransaction = {
  compositeId: string;
  amount: number;
  transactionDate: string;
  activity: string;
  exitPlazaName: string;
  entryPlazaName: string;
  exitPlazaIdName: string;
  entryPlazaIdName: string;
  agency: string;
  isTollorPayment: string;
  maskedCardNumber?: string;
  payType?: string;
};

const extractTransaction: Extractor<EzpassTransaction> = extract.shape({
  compositeId: extract.string,
  amount: extract.number,
  transactionDate: extract.string,
  activity: extract.string,
  exitPlazaName: extract.string,
  entryPlazaName: extract.string,
  exitPlazaIdName: extract.string,
  entryPlazaIdName: extract.string,
  agency: extract.string,
  isTollorPayment: extract.string,
  maskedCardNumber: extract.optional(extract.string),
  payType: extract.optional(extract.string),
});

/**
 * The readable half of a plaza field like "HCT - Hugh L. Carey Tunnel".
 *
 * Splits on the FIRST " - " only: the code is always first, but names can
 * contain the separator themselves ("RKB - RFK Br - Bronx" must keep
 * "RFK Br - Bronx", not collapse to "Bronx"). When the name half is empty
 * the code stands in — some agencies publish plazas as "T353 - " with no
 * name, and the code beats no description at all.
 */
const extractPlazaName = (plazaIdName: string): string | null => {
  const value = (plazaIdName ?? '').trim();
  if (value === '' || value === '-') return null;
  const dashIndex = value.indexOf(' - ');
  if (dashIndex !== -1) {
    return value.substring(dashIndex + 3).trim() || value.substring(0, dashIndex).trim() || null;
  }
  return value.replace(/^-+|-+$/g, '').trim() || null;
};

/**
 * "<where> (<agency>)" — place first so the description still reads as it
 * always has, with the operator appended for context.
 *
 * Deliberately free of hand-maintained tables: agency codes and plaza names
 * are used exactly as E-ZPass publishes them, so a new bridge or a new
 * agency needs no code change. Entry and exit are both shown when they
 * differ, because exit alone collides — three distinct NJ Turnpike trips
 * all render as "I-278/Eliz/Goethal/V" without the entry.
 */
const buildDescription = (transaction: EzpassTransaction): string => {
  if (transaction.activity === 'PAYMENT') {
    const card = (transaction.maskedCardNumber ?? '').trim().slice(-4);
    const payType = (transaction.payType ?? '').trim();
    return card && payType
      ? `EZPass Replenishment (${payType} ••${card})`
      : 'EZPass Replenishment';
  }

  const entry = extractPlazaName(transaction.entryPlazaIdName);
  const exit = extractPlazaName(transaction.exitPlazaIdName);
  const where = entry && exit && entry !== exit ? `${entry} → ${exit}` : (exit ?? entry);

  const activity = (transaction.activity ?? '').trim();
  // A plain toll needs no label; anything else (credits, adjustments) keeps
  // the agency's own wording rather than a mapping we would have to extend.
  const label =
    activity === 'TOLL POSTING' ? where : where ? `${activity} — ${where}` : activity;

  const agency = (transaction.agency ?? '').trim();
  if (!label) return agency ? `Toll (${agency})` : 'Toll';
  return agency ? `${label} (${agency})` : label;
};

const transactionToResult = (transaction: EzpassTransaction): EzpassResult => {
  return {
    amount: Math.round(transaction.amount * 100), // Convert to cents
    date: parseEzpassDate(transaction.transactionDate),
    description: buildDescription(transaction),
    externalSource: EZPASS_SOURCE,
    externalId: transaction.compositeId,
    ezpassData: transaction,
  };
};

const extractEzpass = extract.shape({
  transactionList: extract.shape({
    transaction: extract.array(extractTransaction),
  }),
});

const ezpassFromJson = (json: unknown): ExtractResult<EzpassResult[]> => {
  return extractEzpass(json).map((ezpass) =>
    ezpass.transactionList.transaction.map(transactionToResult),
  );
};

const parseEzpass = (input: string): ExtractResult<EzpassResult[]> => {
  return parseJson(input)
    .map(ezpassFromJson)
    .getOrElseL(() => failure(extractorError.invalidObject(input)));
};

export default parseEzpass;
